import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  adminLogin,
  adminVerify,
  adminUpdateMenu,
  adminUpdateSiteConfig,
  adminUploadImage,
  getMenu,
  getSiteConfig,
  resolveAssetUrl,
} from '../lib/api';
import {
  Trash2,
  Plus,
  Upload,
  LogOut,
  Save,
  Loader2,
  Eye,
  ArrowLeft,
  ArrowRight,
  Star,
} from 'lucide-react';

const TOKEN_KEY = 'vino_admin_token';

const Admin = () => {
  const navigate = useNavigate();
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || '');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [toast, setToast] = useState('');
  const [menu, setMenu] = useState(null);
  const [siteConfig, setSiteConfig] = useState(null);

  useEffect(() => {
    const check = async () => {
      if (!token) return;
      try {
        await adminVerify(token);
        loadData();
      } catch {
        localStorage.removeItem(TOKEN_KEY);
        setToken('');
      }
    };
    check();
    // eslint-disable-next-line
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [m, s] = await Promise.all([getMenu(), getSiteConfig()]);
      // Normalise menu into { categories: [...] }
      const normalised = { categories: Array.isArray(m?.categories) ? m.categories : [] };
      setMenu(normalised);
      // Ensure hero_images exists
      const cfg = { ...s };
      if (!cfg.hero_images || cfg.hero_images.length === 0) {
        cfg.hero_images = cfg.hero_image_url ? [cfg.hero_image_url] : [];
      }
      cfg.rotate_seconds = cfg.rotate_seconds || 6;
      if (!cfg.hours || cfg.hours.length === 0) {
        cfg.hours = [
          { day: 'Wednesday', time: '2pm–late' },
          { day: 'Thursday', time: '2pm–late' },
          { day: 'Friday', time: '12–late' },
          { day: 'Saturday', time: '12–late' },
          { day: 'Sunday', time: '2–late' },
        ];
      }
      setSiteConfig(cfg);
    } finally {
      setLoading(false);
    }
  };

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoading(true);
    try {
      const t = await adminLogin(password);
      localStorage.setItem(TOKEN_KEY, t);
      setToken(t);
      await loadData();
    } catch {
      setLoginError('Incorrect password');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken('');
    setMenu(null);
    setSiteConfig(null);
  };

  const persistConfig = async (nextCfg, successMsg) => {
    setSaving(true);
    try {
      const saved = await adminUpdateSiteConfig(token, nextCfg);
      const cfg = { ...saved };
      if (!cfg.hero_images) cfg.hero_images = nextCfg.hero_images || [];
      cfg.rotate_seconds = cfg.rotate_seconds || nextCfg.rotate_seconds || 6;
      setSiteConfig(cfg);
      if (successMsg) showToast(successMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const { url } = await adminUploadImage(token, f);
        uploaded.push(url);
      }
      const nextImages = [...(siteConfig.hero_images || []), ...uploaded];
      const nextCfg = {
        ...siteConfig,
        hero_images: nextImages,
        hero_image_url: nextImages[0],
      };
      await persistConfig(nextCfg, `Added ${uploaded.length} image${uploaded.length > 1 ? 's' : ''}`);
    } catch {
      showToast('Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const moveImage = (idx, dir) => {
    const list = [...(siteConfig.hero_images || [])];
    const target = idx + dir;
    if (target < 0 || target >= list.length) return;
    [list[idx], list[target]] = [list[target], list[idx]];
    setSiteConfig({ ...siteConfig, hero_images: list, hero_image_url: list[0] });
  };

  const removeImage = (idx) => {
    const list = [...(siteConfig.hero_images || [])];
    list.splice(idx, 1);
    setSiteConfig({ ...siteConfig, hero_images: list, hero_image_url: list[0] || null });
  };

  const setPrimary = (idx) => {
    if (idx === 0) return;
    const list = [...(siteConfig.hero_images || [])];
    const [item] = list.splice(idx, 1);
    list.unshift(item);
    setSiteConfig({ ...siteConfig, hero_images: list, hero_image_url: list[0] });
  };

  const handleSaveHero = () => {
    persistConfig(siteConfig, 'Hero settings saved');
  };

  const updateHour = (idx, field, value) => {
    const list = [...(siteConfig.hours || [])];
    list[idx] = { ...list[idx], [field]: value };
    setSiteConfig({ ...siteConfig, hours: list });
  };

  const addHour = () => {
    const list = [...(siteConfig.hours || []), { day: '', time: '' }];
    setSiteConfig({ ...siteConfig, hours: list });
  };

  const removeHour = (idx) => {
    const list = (siteConfig.hours || []).filter((_, i) => i !== idx);
    setSiteConfig({ ...siteConfig, hours: list });
  };

  const moveHour = (idx, dir) => {
    const list = [...(siteConfig.hours || [])];
    const target = idx + dir;
    if (target < 0 || target >= list.length) return;
    [list[idx], list[target]] = [list[target], list[idx]];
    setSiteConfig({ ...siteConfig, hours: list });
  };

  const handleSaveHours = () => {
    persistConfig(siteConfig, 'Hours saved');
  };

  const handleSaveMenu = async () => {
    setSaving(true);
    try {
      await adminUpdateMenu(token, menu);
      showToast('Wine list saved');
    } finally {
      setSaving(false);
    }
  };

  const updateItem = (catIdx, idx, field, value) => {
    setMenu((prev) => {
      const cats = [...(prev.categories || [])];
      const items = [...(cats[catIdx]?.items || [])];
      items[idx] = { ...items[idx], [field]: value };
      cats[catIdx] = { ...cats[catIdx], items };
      return { ...prev, categories: cats };
    });
  };

  const addItem = (catIdx) =>
    setMenu((prev) => {
      const cats = [...(prev.categories || [])];
      cats[catIdx] = {
        ...cats[catIdx],
        items: [...(cats[catIdx]?.items || []), { name: '', price: '' }],
      };
      return { ...prev, categories: cats };
    });

  const removeItem = (catIdx, idx) =>
    setMenu((prev) => {
      const cats = [...(prev.categories || [])];
      cats[catIdx] = {
        ...cats[catIdx],
        items: (cats[catIdx]?.items || []).filter((_, i) => i !== idx),
      };
      return { ...prev, categories: cats };
    });

  const renameCategory = (catIdx, name) =>
    setMenu((prev) => {
      const cats = [...(prev.categories || [])];
      cats[catIdx] = { ...cats[catIdx], name };
      return { ...prev, categories: cats };
    });

  const addCategory = () =>
    setMenu((prev) => ({
      ...prev,
      categories: [...(prev.categories || []), { name: 'NEW CATEGORY', items: [] }],
    }));

  const removeCategory = (catIdx) => {
    const cat = menu?.categories?.[catIdx];
    const count = cat?.items?.length || 0;
    const label = cat?.name || 'this category';
    if (count > 0 && !window.confirm(`Remove "${label}" and its ${count} wines?`)) return;
    setMenu((prev) => ({
      ...prev,
      categories: (prev.categories || []).filter((_, i) => i !== catIdx),
    }));
  };

  const moveCategory = (catIdx, dir) => {
    setMenu((prev) => {
      const cats = [...(prev.categories || [])];
      const target = catIdx + dir;
      if (target < 0 || target >= cats.length) return prev;
      [cats[catIdx], cats[target]] = [cats[target], cats[catIdx]];
      return { ...prev, categories: cats };
    });
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-foresta flex items-center justify-center px-6">
        <div className="w-full max-w-sm">
          <div className="text-center mb-10">
            <div className="vino-mono-medium text-bianco text-[10px] tracking-[0.5em] mb-3">MMXXV</div>
            <div className="vino-display text-bianco text-5xl tracking-[0.08em]">VINO</div>
            <div className="vino-script text-bianco text-2xl -mt-1">by tonino</div>
            <div className="vino-mono-medium text-ruggine text-[10px] tracking-[0.4em] mt-4">ADMIN</div>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="w-full bg-transparent border border-bianco/30 focus:border-bianco text-bianco vino-mono px-4 py-3 outline-none transition-colors placeholder:text-bianco/40"
              autoFocus
            />
            {loginError && (
              <div className="vino-mono text-ruggine text-[11px] tracking-wide">{loginError}</div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-ruggine hover:bg-ruggine/85 text-bianco vino-mono-medium tracking-[0.3em] text-xs py-3 transition-colors disabled:opacity-60"
            >
              {loading ? 'SIGNING IN…' : 'SIGN IN'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="w-full vino-mono text-bianco/60 hover:text-bianco text-[11px] tracking-[0.3em] py-2 transition-colors"
            >
              ← BACK TO SITE
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (loading || !menu || !siteConfig) {
    return (
      <div className="min-h-screen bg-foresta flex items-center justify-center">
        <Loader2 className="text-bianco animate-spin" size={28} />
      </div>
    );
  }

  const heroImages = siteConfig.hero_images || [];

  return (
    <div className="min-h-screen bg-foresta text-bianco">
      {/* Top bar */}
      <div className="sticky top-0 z-30 bg-foresta/95 backdrop-blur border-b border-bianco/10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <span className="vino-display text-lg tracking-[0.3em]">VINO</span>
            <span className="vino-script text-lg">by tonino</span>
            <span className="vino-mono-medium text-ruggine text-[10px] tracking-[0.4em] ml-2">ADMIN</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/')}
              className="vino-mono-medium text-[10px] tracking-[0.3em] px-3 py-2 border border-bianco/20 hover:border-bianco text-bianco/80 hover:text-bianco transition-colors flex items-center gap-2"
            >
              <Eye size={14} /> VIEW SITE
            </button>
            <button
              onClick={handleLogout}
              className="vino-mono-medium text-[10px] tracking-[0.3em] px-3 py-2 border border-bianco/20 hover:border-ruggine hover:text-ruggine text-bianco/80 transition-colors flex items-center gap-2"
            >
              <LogOut size={14} /> LOG OUT
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-10 space-y-12">
        {/* Header gallery */}
        <section>
          <div className="flex items-baseline gap-3 mb-4">
            <div className="vino-mono-medium text-ruggine text-[10px] tracking-[0.4em]">01</div>
            <h2 className="vino-display text-2xl tracking-[0.15em]">HEADER GALLERY</h2>
          </div>
          <p className="vino-mono text-bianco/70 text-xs mb-6 max-w-2xl">
            Upload one or more hero images (JPG/PNG/WEBP, up to 10MB each). The site cross-fades between them. Reorder with the arrows — the first image is shown on load.
          </p>

          {/* Upload button */}
          <label className="block mb-8 cursor-pointer">
            <div className="border border-dashed border-bianco/30 hover:border-ruggine transition-colors p-8 text-center">
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleUpload}
                className="hidden"
                disabled={uploading}
                multiple
              />
              <div className="flex flex-col items-center gap-3 pointer-events-none">
                {uploading ? (
                  <Loader2 className="animate-spin text-ruggine" size={28} />
                ) : (
                  <Upload className="text-bianco" size={28} />
                )}
                <div className="vino-mono text-xs text-bianco/80">
                  {uploading ? 'Uploading…' : 'Click to select one or more images'}
                </div>
                <div className="vino-mono text-[10px] text-bianco/50">
                  JPG, PNG, or WEBP · max 10MB each
                </div>
              </div>
            </div>
          </label>

          {/* Gallery grid */}
          {heroImages.length === 0 ? (
            <div className="vino-mono text-bianco/50 text-[12px] italic">
              No images yet — upload above to get started.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
              {heroImages.map((url, idx) => (
                <div key={url + idx} className="group relative aspect-[16/10] bg-black/40 overflow-hidden border border-bianco/10">
                  <img
                    src={resolveAssetUrl(url)}
                    alt={`Hero ${idx + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 left-2 flex items-center gap-1">
                    <span className="vino-mono-medium text-[9px] tracking-[0.35em] bg-foresta/85 px-2 py-1">
                      {String(idx + 1).padStart(2, '0')}
                    </span>
                    {idx === 0 && (
                      <span className="vino-mono-medium text-[9px] tracking-[0.35em] bg-ruggine text-bianco px-2 py-1 flex items-center gap-1">
                        <Star size={10} /> PRIMARY
                      </span>
                    )}
                  </div>
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-black/45 flex items-center justify-center gap-2">
                    <button
                      onClick={() => moveImage(idx, -1)}
                      disabled={idx === 0}
                      className="p-2 bg-foresta text-bianco hover:bg-ruggine disabled:opacity-30 transition-colors"
                      aria-label="Move left"
                    >
                      <ArrowLeft size={14} />
                    </button>
                    {idx !== 0 && (
                      <button
                        onClick={() => setPrimary(idx)}
                        className="p-2 bg-foresta text-bianco hover:bg-ruggine transition-colors"
                        aria-label="Make primary"
                        title="Make primary"
                      >
                        <Star size={14} />
                      </button>
                    )}
                    <button
                      onClick={() => moveImage(idx, 1)}
                      disabled={idx === heroImages.length - 1}
                      className="p-2 bg-foresta text-bianco hover:bg-ruggine disabled:opacity-30 transition-colors"
                      aria-label="Move right"
                    >
                      <ArrowRight size={14} />
                    </button>
                    <button
                      onClick={() => removeImage(idx)}
                      className="p-2 bg-foresta text-bianco hover:bg-ruggine transition-colors"
                      aria-label="Remove"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="grid md:grid-cols-3 gap-6 mt-4">
            <div>
              <div className="vino-mono-medium text-[10px] tracking-[0.35em] mb-2">TAGLINE (SCRIPT)</div>
              <input
                type="text"
                value={siteConfig.hero_tagline || ''}
                onChange={(e) => setSiteConfig({ ...siteConfig, hero_tagline: e.target.value })}
                className="w-full bg-transparent border border-bianco/25 focus:border-bianco text-bianco vino-mono px-3 py-2 outline-none transition-colors text-sm"
              />
            </div>
            <div>
              <div className="vino-mono-medium text-[10px] tracking-[0.35em] mb-2">YEAR BADGE</div>
              <input
                type="text"
                value={siteConfig.since_year || ''}
                onChange={(e) => setSiteConfig({ ...siteConfig, since_year: e.target.value })}
                className="w-full bg-transparent border border-bianco/25 focus:border-bianco text-bianco vino-mono px-3 py-2 outline-none transition-colors text-sm"
              />
            </div>
            <div>
              <div className="vino-mono-medium text-[10px] tracking-[0.35em] mb-2">ROTATE EVERY (SECONDS)</div>
              <input
                type="number"
                min={2}
                max={30}
                value={siteConfig.rotate_seconds || 6}
                onChange={(e) =>
                  setSiteConfig({
                    ...siteConfig,
                    rotate_seconds: parseInt(e.target.value || '6', 10),
                  })
                }
                className="w-full bg-transparent border border-bianco/25 focus:border-bianco text-bianco vino-mono px-3 py-2 outline-none transition-colors text-sm"
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={handleSaveHero}
              disabled={saving}
              className="bg-ruggine hover:bg-ruggine/85 text-bianco vino-mono-medium tracking-[0.3em] text-xs px-6 py-3 transition-colors flex items-center gap-2 disabled:opacity-60"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              SAVE HERO SETTINGS
            </button>
          </div>
        </section>

        <div className="h-px bg-bianco/10" />

        {/* Hours section */}
        <section>
          <div className="flex items-baseline gap-3 mb-4">
            <div className="vino-mono-medium text-ruggine text-[10px] tracking-[0.4em]">02</div>
            <h2 className="vino-display text-2xl tracking-[0.15em]">OPENING HOURS</h2>
          </div>
          <p className="vino-mono text-bianco/70 text-xs mb-6 max-w-2xl">
            Edit the day/time pairs shown in the "Get In Touch" section. Add days, reorder them, or remove any that don't apply.
          </p>

          <div className="max-w-2xl space-y-2">
            {(siteConfig.hours || []).map((h, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <span className="vino-mono-medium text-bianco/40 text-[10px] tracking-[0.3em] w-6 text-right">
                  {String(idx + 1).padStart(2, '0')}
                </span>
                <input
                  type="text"
                  value={h.day}
                  onChange={(e) => updateHour(idx, 'day', e.target.value)}
                  placeholder="Wednesday"
                  className="flex-1 bg-black/20 border border-bianco/15 focus:border-bianco text-bianco vino-mono text-[12px] px-3 py-2 outline-none transition-colors"
                />
                <input
                  type="text"
                  value={h.time}
                  onChange={(e) => updateHour(idx, 'time', e.target.value)}
                  placeholder="2pm–late"
                  className="flex-1 bg-black/20 border border-bianco/15 focus:border-bianco text-bianco vino-mono text-[12px] px-3 py-2 outline-none transition-colors"
                />
                <button
                  onClick={() => moveHour(idx, -1)}
                  disabled={idx === 0}
                  className="text-bianco/50 hover:text-bianco disabled:opacity-20 transition-colors p-1"
                  aria-label="Move up"
                >
                  <ArrowLeft size={14} className="rotate-90" />
                </button>
                <button
                  onClick={() => moveHour(idx, 1)}
                  disabled={idx === (siteConfig.hours || []).length - 1}
                  className="text-bianco/50 hover:text-bianco disabled:opacity-20 transition-colors p-1"
                  aria-label="Move down"
                >
                  <ArrowRight size={14} className="rotate-90" />
                </button>
                <button
                  onClick={() => removeHour(idx)}
                  className="text-bianco/50 hover:text-ruggine transition-colors p-1"
                  aria-label="Remove"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            {(!siteConfig.hours || siteConfig.hours.length === 0) && (
              <div className="vino-mono text-bianco/40 text-[11px] italic">No hours yet.</div>
            )}
          </div>

          <div className="mt-6 flex items-center justify-between max-w-2xl">
            <button
              onClick={addHour}
              className="vino-mono-medium text-[10px] tracking-[0.3em] text-ruggine hover:text-bianco transition-colors flex items-center gap-1"
            >
              <Plus size={12} /> ADD DAY
            </button>
            <button
              onClick={handleSaveHours}
              disabled={saving}
              className="bg-ruggine hover:bg-ruggine/85 text-bianco vino-mono-medium tracking-[0.3em] text-xs px-6 py-3 transition-colors flex items-center gap-2 disabled:opacity-60"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              SAVE HOURS
            </button>
          </div>
        </section>

        <div className="h-px bg-bianco/10" />

        {/* Menu section */}
        <section>
          <div className="flex items-baseline gap-3 mb-4">
            <div className="vino-mono-medium text-ruggine text-[10px] tracking-[0.4em]">03</div>
            <h2 className="vino-display text-2xl tracking-[0.15em]">WINE LIST</h2>
          </div>
          <p className="vino-mono text-bianco/70 text-xs mb-6 max-w-2xl">
            Rename, reorder, add or remove categories. Then edit, add or remove wines within each category. Changes appear on the public site instantly after saving.
          </p>

          <div className="space-y-6">
            {(menu.categories || []).map((cat, catIdx) => (
              <div key={catIdx} className="border border-bianco/10 p-5">
                <div className="flex items-center gap-2 mb-4">
                  <span className="vino-mono-medium text-bianco/40 text-[10px] tracking-[0.3em] w-6 text-right">
                    {String(catIdx + 1).padStart(2, '0')}
                  </span>
                  <input
                    type="text"
                    value={cat.name}
                    onChange={(e) => renameCategory(catIdx, e.target.value)}
                    placeholder="CATEGORY NAME"
                    className="flex-1 bg-transparent border-b border-bianco/20 focus:border-bianco text-bianco vino-mono-medium tracking-[0.4em] uppercase text-sm px-1 py-2 outline-none transition-colors"
                  />
                  <button
                    onClick={() => moveCategory(catIdx, -1)}
                    disabled={catIdx === 0}
                    className="text-bianco/50 hover:text-bianco disabled:opacity-20 transition-colors p-1"
                    aria-label="Move up"
                  >
                    <ArrowLeft size={14} className="rotate-90" />
                  </button>
                  <button
                    onClick={() => moveCategory(catIdx, 1)}
                    disabled={catIdx === (menu.categories || []).length - 1}
                    className="text-bianco/50 hover:text-bianco disabled:opacity-20 transition-colors p-1"
                    aria-label="Move down"
                  >
                    <ArrowRight size={14} className="rotate-90" />
                  </button>
                  <button
                    onClick={() => removeCategory(catIdx)}
                    className="text-bianco/50 hover:text-ruggine transition-colors p-1"
                    aria-label="Remove category"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>

                <div className="space-y-2">
                  {(cat.items || []).map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={item.name}
                        onChange={(e) => updateItem(catIdx, idx, 'name', e.target.value)}
                        placeholder="Wine name, region, country"
                        className="flex-1 bg-black/20 border border-bianco/15 focus:border-bianco text-bianco vino-mono text-[12px] px-3 py-2 outline-none transition-colors"
                      />
                      <input
                        type="text"
                        value={item.price}
                        onChange={(e) => updateItem(catIdx, idx, 'price', e.target.value)}
                        placeholder="28 / 5.5"
                        className="w-24 bg-black/20 border border-bianco/15 focus:border-bianco text-bianco vino-mono text-[12px] px-3 py-2 outline-none transition-colors text-right"
                      />
                      <button
                        onClick={() => removeItem(catIdx, idx)}
                        className="text-bianco/50 hover:text-ruggine transition-colors p-1"
                        aria-label="Remove"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                  {(!cat.items || cat.items.length === 0) && (
                    <div className="vino-mono text-bianco/40 text-[11px] italic">No items yet.</div>
                  )}
                </div>

                <div className="mt-4">
                  <button
                    onClick={() => addItem(catIdx)}
                    className="vino-mono-medium text-[10px] tracking-[0.3em] text-ruggine hover:text-bianco transition-colors flex items-center gap-1"
                  >
                    <Plus size={12} /> ADD WINE
                  </button>
                </div>
              </div>
            ))}

            {(!menu.categories || menu.categories.length === 0) && (
              <div className="vino-mono text-bianco/40 text-[12px] italic border border-dashed border-bianco/15 p-6 text-center">
                No categories yet — add one to get started.
              </div>
            )}
          </div>

          <div className="mt-6 flex items-center justify-between">
            <button
              onClick={addCategory}
              className="vino-mono-medium text-[10px] tracking-[0.3em] text-ruggine hover:text-bianco transition-colors flex items-center gap-1"
            >
              <Plus size={12} /> ADD CATEGORY
            </button>
            <button
              onClick={handleSaveMenu}
              disabled={saving}
              className="bg-ruggine hover:bg-ruggine/85 text-bianco vino-mono-medium tracking-[0.3em] text-xs px-6 py-3 transition-colors flex items-center gap-2 disabled:opacity-60"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              SAVE WINE LIST
            </button>
          </div>
        </section>
      </div>

      {/* Toast */}
      <div
        className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 transition-all duration-300 ${
          toast ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
      >
        <div className="bg-bianco text-foresta vino-mono-medium text-[11px] tracking-[0.3em] px-5 py-3 shadow-lg">
          {toast}
        </div>
      </div>
    </div>
  );
};

export default Admin;
